import React from 'react';
import {
  Breadcrumbs as MuiBreadcrumbs,
  BreadcrumbsProps as MuiBreadcrumbsProps,
  Link,
  Typography,
  Chip,
} from '@mui/material';
import {
  NavigateNext,
  ChevronRight,
  ArrowForwardIos,
  Home,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledBreadcrumbs = styled(MuiBreadcrumbs, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'default' | 'outlined' | 'contained' }>(
  ({ theme, variant = 'default' }) => ({
    '& .MuiBreadcrumbs-li': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiBreadcrumbs-separator': {
      margin: theme.spacing(0, 1),
      color: theme.palette.text.secondary,
    },
    ...(variant === 'outlined' && {
      padding: theme.spacing(1, 2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
    }),
    ...(variant === 'contained' && {
      padding: theme.spacing(1, 2),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.shape.borderRadius,
      ...(theme.palette.mode === 'dark' && {
        backgroundColor: theme.palette.grey[800],
      }),
    }),
  })
);

const BreadcrumbLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  textDecoration: 'none',
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  '&:focus': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
    borderRadius: 2,
  },
}));

const BreadcrumbText = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.primary,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 500,
}));

export interface BreadcrumbItem {
  /**
   * The text to display
   */
  label: string;
  /**
   * URL to navigate to (if clickable)
   */
  href?: string;
  /**
   * Click handler (alternative to href)
   */
  onClick?: () => void;
  /**
   * Icon to display before the label
   */
  icon?: React.ReactNode;
  /**
   * Whether this item is disabled
   */
  disabled?: boolean;
}

export interface BreadcrumbsProps extends Omit<MuiBreadcrumbsProps, 'children'> {
  /**
   * Array of breadcrumb items
   */
  items: BreadcrumbItem[];
  /**
   * Visual variant
   */
  variant?: 'default' | 'outlined' | 'contained';
  /**
   * Separator icon/element
   */
  separatorIcon?: 'chevron' | 'arrow' | 'slash' | React.ReactNode;
  /**
   * Whether to show home icon on first item
   */
  showHomeIcon?: boolean;
  /**
   * Maximum number of items to show before collapsing
   */
  maxItems?: number;
  /**
   * Whether to show items as chips instead of links
   */
  chipVariant?: boolean;
  /**
   * Size of the breadcrumbs
   */
  size?: 'small' | 'medium' | 'large';
}

const getSeparatorIcon = (separatorIcon: BreadcrumbsProps['separatorIcon']) => {
  if (React.isValidElement(separatorIcon)) {
    return separatorIcon;
  }
  
  switch (separatorIcon) {
    case 'chevron':
      return <ChevronRight fontSize="small" />;
    case 'arrow':
      return <ArrowForwardIos fontSize="small" />;
    case 'slash':
      return '/';
    default:
      return <NavigateNext fontSize="small" />;
  }
};

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  variant = 'default',
  separatorIcon = 'chevron',
  showHomeIcon = false,
  maxItems,
  chipVariant = false,
  size = 'medium',
  ...props
}) => {
  const separator = getSeparatorIcon(separatorIcon);

  const renderBreadcrumbItem = (item: BreadcrumbItem, index: number, isLast: boolean) => {
    const content = (
      <>
        {showHomeIcon && index === 0 && <Home fontSize="small" />}
        {item.icon}
        {item.label}
      </>
    );

    if (chipVariant) {
      return (
        <Chip
          key={index}
          label={content}
          variant={isLast ? 'filled' : 'outlined'}
          size={size === 'large' ? 'medium' : 'small'}
          clickable={!isLast && !item.disabled && (!!item.href || !!item.onClick)}
          onClick={!isLast && !item.disabled ? item.onClick : undefined}
          component={!isLast && item.href && !item.disabled ? 'a' : 'div'}
          href={!isLast && item.href && !item.disabled ? item.href : undefined}
          disabled={item.disabled}
          color={isLast ? 'primary' : 'default'}
        />
      );
    }

    if (isLast || item.disabled) {
      return (
        <BreadcrumbText key={index} variant="body2">
          {content}
        </BreadcrumbText>
      );
    }

    return (
      <BreadcrumbLink
        key={index}
        href={item.href}
        onClick={item.onClick}
        underline="none"
        {...(item.href ? {} : { component: 'button' })}
      >
        {content}
      </BreadcrumbLink>
    );
  };

  return (
    <StyledBreadcrumbs
      variant={variant}
      separator={separator}
      maxItems={maxItems}
      {...props}
    >
      {items.map((item, index) => 
        renderBreadcrumbItem(item, index, index === items.length - 1)
      )}
    </StyledBreadcrumbs>
  );
}; 